import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { RowData } from '../App';
import type { LtrRtlControl } from '../classes/Block';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './DataGrid.css'; // Import your custom CSS
import { CellMouseDownEvent, CellMouseOverEvent, CellValueChangedEvent, ColDef, ColGroupDef, ColumnMovedEvent, GridApi, GridOptions, GridReadyEvent, RowDragEndEvent } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'; 
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { trackEvent } from '../statsig';
import {
  buildWysiwygGridModel,
  createWysiwygRowsToAdd,
  getWysiwygCellField,
  getWysiwygCellSource,
  getWysiwygIndexesFromRows,
  getWysiwygVisualColumnIndex,
  reorderWysiwygColumns,
  reorderWysiwygChunks,
  swapRowCellValues,
  updateWysiwygCellValue,
  WysiwygGridModel,
  WysiwygVisualRow,
} from '../utils/dataGridWysiwygLayout';
ModuleRegistry.registerModules([AllCommunityModule]);

const getColumnField = (colDef: ColDef | ColGroupDef | undefined): string | undefined => {
  if (!colDef || !('field' in colDef)) return undefined;
  return typeof colDef.field === 'string' ? colDef.field : undefined;
};

const handleMaybePromise = (result: void | Promise<void>, label: string): void => {
  if (result instanceof Promise) {
    result.catch((error: unknown) => {
      console.error(label, error);
    });
  }
};

const haveSameRowOrder = (nextRows: RowData[], currentRows: RowData[]): boolean =>
  nextRows.length === currentRows.length && nextRows.every((row: RowData, index: number) => row === currentRows[index]);

interface DataGridProps {
  rowData: RowData[];
  columnDefs: ColDef[];
  onRowDataChange?: (newRowData: RowData[]) => void | Promise<void>;
  isLogoBlock?: boolean;
  logoOptions?: string[];
  isSingleColumn?: boolean;
  isSongBlock?: boolean;
  blockId?: string;
  blockName?: string;
  numColumns?: number;
  labelValueAlignment?: 'horizontal' | 'vertical';
  ltrRtlControl?: LtrRtlControl;
}

interface DataGridDragCell {
  sourceIndex: number;
  sourceField: string;
  rowIndex: number;
  colId: string;
}

interface PendingCellDrag {
  cell: DataGridDragCell;
  startX: number;
  startY: number;
  isDragging: boolean;
  sourceElement: HTMLElement | null;
  lastDropTargetElement: HTMLElement | null;
  lastDropTargetCell: DataGridDragCell | null;
}

const CELL_SWAP_DRAG_THRESHOLD_PX = 6;

const DataGrid: React.FC<DataGridProps> = ({
  rowData,
  columnDefs,
  onRowDataChange,
  isLogoBlock,
  isSongBlock,
  logoOptions,
  isSingleColumn,
  blockId,
  blockName,
  numColumns = 1,
  labelValueAlignment = 'horizontal',
  ltrRtlControl = 'auto',
}) => {
  const [gridApi, setGridApi] = useState<GridApi<RowData> | null>(null);
  const [isUrlDialogOpen, setIsUrlDialogOpen] = useState<boolean>(false);
  const [urlInputValue, setUrlInputValue] = useState<string>('');
  const [urlInputError, setUrlInputError] = useState<string>('');
  const [pendingEdit, setPendingEdit] = useState<{ rowIndex: number; field: string; oldValue: string } | null>(null);
  const gridReadyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCellDragRef = useRef<PendingCellDrag | null>(null);
  const suppressCellDragClickUntilRef = useRef<number>(0);

  const defaultColDef: ColDef<RowData> = useMemo<ColDef<RowData>>(() => ({
    flex: 1,
    minWidth: 100,
    resizable: true,
    editable: true,
    filter: false,
    sortable: false,
  }), []);

  const rowSelection: NonNullable<GridOptions<RowData>['rowSelection']> = useMemo<NonNullable<GridOptions<RowData>['rowSelection']>>(() => ({
    mode: 'multiRow',
    checkboxes: false,
    headerCheckbox: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  }), []);

  useEffect(() => {
    return () => {
      if (gridReadyTimeoutRef.current) {
        clearTimeout(gridReadyTimeoutRef.current);
        gridReadyTimeoutRef.current = null;
      }
      document.body.classList.remove('data-grid-cell-drag-active');
      suppressCellDragClickUntilRef.current = 0;
    };
  }, []);

  useEffect(() => {
    trackEvent('data_grid_viewed', {
      block_id: blockId,
      block_name: blockName,
      row_count: rowData.length,
      column_count: columnDefs.length,
      is_logo_block: Boolean(isLogoBlock),
      is_song_block: Boolean(isSongBlock),
      is_single_column: Boolean(isSingleColumn),
      label_value_alignment: labelValueAlignment,
      ltr_rtl_control: ltrRtlControl,
    }, {
      dedupeKey: `${blockId ?? 'no_block'}:${rowData.length}:${columnDefs.length}`,
      dedupeMs: 2000,
    });
  }, [blockId, blockName, columnDefs.length, isLogoBlock, isSingleColumn, isSongBlock, labelValueAlignment, ltrRtlControl, rowData.length]);
    
  const baseColumnDefs: ColDef[] = useMemo<ColDef[]>(
    () => (Array.isArray(columnDefs) ? [...columnDefs] : []),
    [columnDefs]
  );
  const wysiwygModel: WysiwygGridModel = useMemo<WysiwygGridModel>(
    () => buildWysiwygGridModel({
      rowData,
      columnDefs: baseColumnDefs,
      numColumns,
      isLogoBlock,
      isSingleColumn,
      isSongBlock,
      labelValueAlignment,
      ltrRtlControl,
    }),
    [baseColumnDefs, isLogoBlock, isSingleColumn, isSongBlock, labelValueAlignment, ltrRtlControl, numColumns, rowData]
  );
  const displayRowData: RowData[] | WysiwygVisualRow[] = useMemo<RowData[] | WysiwygVisualRow[]>(() => {
    return wysiwygModel.enabled ? wysiwygModel.rowData : rowData;
  }, [rowData, wysiwygModel]);

  // Determine which data to use
  const displayColumnDefs = useMemo<Array<ColDef | ColGroupDef>>(() => {
    if (wysiwygModel.enabled) return wysiwygModel.columnDefs;

    const withRowDragHandle = (defs: ColDef[]): ColDef[] =>
      defs.map((colDef: ColDef, index: number) => (index === 0 ? { ...colDef, rowDrag: true } : colDef));

    const base: ColDef[] = [...baseColumnDefs];
    if ((!isLogoBlock && !isSingleColumn) || isSongBlock) return withRowDragHandle(base);

    if (isSingleColumn) {
      // Make the 1st column disabled
      return withRowDragHandle([{...base[0], headerName: 'Label - Not Shown', editable: false, cellClass: 'ag-cell-not-editable', headerClass: 'ag-header-cell-not-editable'}, base[1]]);
    }

    // From here it's just for logo blocks
    const enterUrlLabel: string = 'Enter URL...';
    const providedValues: string[] = Array.isArray(logoOptions) ? logoOptions : [];
    const allowedValues: string[] = [
      enterUrlLabel,
      ...providedValues.sort(),
    ];

    // For logo blocks, use only the URL/filename column
    // Find the label column: prefer 'col1' or header 'Label', fallback to index 1
    let labelColumnIndex: number = base.findIndex((col: ColDef) => !!col && (col.field === 'col1'));
    if (labelColumnIndex === -1) {
      labelColumnIndex = base.findIndex((col: ColDef) => !!col && typeof col.headerName === 'string' && (col.headerName as string).toLowerCase() === 'label');
    }
    if (labelColumnIndex === -1 && base.length >= 2) {
      labelColumnIndex = 1;
    }

    if (labelColumnIndex >= 0 && base[labelColumnIndex]) {
      const urlColumn: ColDef = {
        ...base[labelColumnIndex],
        headerName: 'URL/Filename',
        editable: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: allowedValues },
        sortable: false,
        filter: false,
      };

      // For logo blocks, show only the URL column
      return withRowDragHandle([urlColumn]);
    }

    return withRowDragHandle(base);
  }, [baseColumnDefs, isLogoBlock, isSingleColumn, isSongBlock, logoOptions, wysiwygModel]);

  // useEffect(() => {
  //   // console.log('DataGrid received data:', { 
  //   //   rowCount: rowData.length, 
  //   //   columnCount: columnDefs.length,
  //   // });
    
  //   // If we have a grid API, tell it to refresh when data changes
  //   /*
  //   if (gridApi) {
  //     gridApi.setColumnDefs(displayColumnDefs);
  //     gridApi.setRowData(displayRowData);
  //   }
  //     */
  // }, [rowData, columnDefs, gridApi, displayColumnDefs, displayRowData]);



  const onUpdateData = (event: CellValueChangedEvent<RowData>) => {
    if (wysiwygModel.enabled) {
      const eventField: string = event.column.getColId();
      const visualRow: WysiwygVisualRow | undefined = event.data as WysiwygVisualRow | undefined;
      if (!visualRow) return;
      const update = updateWysiwygCellValue(rowData, visualRow, eventField, wysiwygModel.sourceFields, event.newValue);
      if (!update) return;

      trackEvent('data_grid_cell_edited', {
        block_id: blockId,
        block_name: blockName,
        row_index: update.sourceIndex,
        visual_row_index: visualRow.__wysiwygRowIndex,
        visual_column_index: update.visualColumnIndex,
        field_name: update.sourceField,
        block_type: isSongBlock ? 'song' : isSingleColumn ? 'single_cell' : 'standard',
        wysiwyg_multicolumn: true,
        wysiwyg_layout: wysiwygModel.layoutMode,
      }, {
        dedupeKey: `${blockId ?? 'no_block'}:${update.sourceField}:${update.sourceIndex}`,
      });
      const result = onRowDataChange?.(update.nextRows);
      if (result instanceof Promise) {
        result.catch(error => {
          console.error('Error in onRowDataChange:', error);
        });
      }

      setTimeout(() => {
        if (gridApi && event.rowIndex !== undefined && event.rowIndex !== null) {
          const nextRowIndex: number = Math.min(event.rowIndex + 1, displayRowData.length - 1);
          gridApi.setFocusedCell(nextRowIndex, eventField);
          gridApi.ensureIndexVisible(nextRowIndex);
        }
      }, 50);
      return;
    }

    // Special handling for logo blocks: allow a custom URL entry via a sentinel option
    if (isLogoBlock) {
      const enterUrlLabel: string = 'Enter URL...';
      const valueField: string | undefined = getColumnField(displayColumnDefs[0]);
      const eventField: string | undefined = typeof event.colDef.field === 'string' ? (event.colDef.field as string) : undefined;
      const isValueColumn: boolean = !!valueField && !!eventField && (valueField === eventField);
      if (isValueColumn && event.newValue === enterUrlLabel) {
        trackEvent('data_grid_logo_url_prompt_opened', {
          block_id: blockId,
          block_name: blockName,
          row_index: event.rowIndex ?? 0,
        });
        // Revert the temporary select value and open dialog for URL entry
        const oldValue: string = typeof event.oldValue === 'string' ? event.oldValue : '';
        if (eventField) {
          event.node.setDataValue(eventField, oldValue);
        }
        setPendingEdit({ rowIndex: event.rowIndex ?? 0, field: eventField || valueField || 'col1', oldValue });
        setUrlInputValue('');
        setUrlInputError('');
        setIsUrlDialogOpen(true);
        return;
      }
    }

    const updatedRowData: RowData[] = [...rowData];
    const rowIndex: number = event.rowIndex ?? 0;
    const originalRow: RowData = rowData[rowIndex] ?? {};
    let mergedRow: RowData = { ...originalRow, ...event.data };
    
    updatedRowData[rowIndex] = mergedRow;
    trackEvent('data_grid_cell_edited', {
      block_id: blockId,
      block_name: blockName,
      row_index: rowIndex,
      field_name: event.colDef.field ?? event.column.getColId(),
      block_type: isLogoBlock ? 'logo' : isSongBlock ? 'song' : isSingleColumn ? 'single_cell' : 'standard',
    }, {
      dedupeKey: `${blockId ?? 'no_block'}:${event.colDef.field ?? event.column.getColId()}:${rowIndex}`,
    });
    const result = onRowDataChange?.(updatedRowData);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onRowDataChange:', error);
      });
    }
    
    // Maintain focus position after cell value change
    setTimeout(() => {
      if (gridApi && event.rowIndex !== undefined && event.rowIndex !== null) {
        const currentRowIndex = event.rowIndex;
        const currentColField = event.column.getColId();
        
        // Try to focus the same cell, or move to next row if at the end
        if (currentRowIndex < updatedRowData.length - 1) {
          // Move to next row, same column
          gridApi.setFocusedCell(currentRowIndex + 1, currentColField);
        } else {
          // Stay in the same cell if at the last row
          gridApi.setFocusedCell(currentRowIndex, currentColField);
        }
        
        // Ensure the focused cell is visible
        gridApi.ensureIndexVisible(currentRowIndex);
      }
    }, 50);
  }

  const handleUrlDialogClose = () => {
    setIsUrlDialogOpen(false);
    setPendingEdit(null);
    setUrlInputValue('');
    setUrlInputError('');
  };

  const handleUrlDialogSave = () => {
    const input: string = urlInputValue.trim();
    const isValidUrl: boolean = /^https?:\/\/.+/.test(input);
    if (!isValidUrl) {
      setUrlInputError('Please enter a valid URL starting with http:// or https://');
      return;
    }
    if (!pendingEdit) {
      handleUrlDialogClose();
      return;
    }

    const { rowIndex, field } = pendingEdit;

    // Update underlying data and notify parent
    const updatedRowData: RowData[] = [...rowData];
    const originalRow: RowData = updatedRowData[rowIndex] ?? {};
    let mergedRow: RowData = { ...originalRow, [field]: input };
    
    // Ensure col2 has default values for logo blocks
    if (isLogoBlock && !('col2' in mergedRow)) {
      mergedRow = { ...mergedRow, col2: JSON.stringify({ scale: 100, xOffset: 0, yOffset: 0, autoCrop: false }) };
    }
    
    updatedRowData[rowIndex] = mergedRow;
    trackEvent('data_grid_logo_url_saved', {
      block_id: blockId,
      block_name: blockName,
      row_index: rowIndex,
    });
    const result = onRowDataChange?.(updatedRowData);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onRowDataChange:', error);
      });
    }

    // Also update the grid cell immediately for responsiveness
    if (gridApi) {
      const node = gridApi.getDisplayedRowAtIndex(rowIndex);
      if (node) node.setDataValue(field, input);
    }

    handleUrlDialogClose();
  };

  const addRow = () => {
    if (wysiwygModel.enabled) {
      const fieldsToInitialize: string[] = wysiwygModel.allSourceFields.length > 0
        ? wysiwygModel.allSourceFields
        : wysiwygModel.sourceFields;
      const rowsToAdd: RowData[] = createWysiwygRowsToAdd(
        wysiwygModel.columnCount,
        fieldsToInitialize,
        rowData.length
      );
      const updatedRowData: RowData[] = [...rowData, ...rowsToAdd];
      trackEvent('data_grid_row_added', {
        block_id: blockId,
        block_name: blockName,
        next_row_count: updatedRowData.length,
        added_rendered_columns: wysiwygModel.columnCount,
        wysiwyg_multicolumn: true,
        wysiwyg_layout: wysiwygModel.layoutMode,
      });
      const result = onRowDataChange?.(updatedRowData);
      if (result instanceof Promise) {
        result.catch(error => {
          console.error('Error in onRowDataChange:', error);
        });
      }
      setTimeout(() => {
        if (gridApi && wysiwygModel.sourceFields[0]) {
          const newChunkIndex: number = Math.max(0, Math.ceil(updatedRowData.length / wysiwygModel.columnCount) - 1);
          const newVisualRowIndex: number = wysiwygModel.layoutMode === 'vertical'
            ? newChunkIndex * Math.max(wysiwygModel.sourceFields.length, 1)
            : newChunkIndex;
          const firstCellField: string = wysiwygModel.layoutMode === 'vertical'
            ? 'wysiwyg_vertical_0'
            : getWysiwygCellField(0, wysiwygModel.sourceFields[0]);
          gridApi.setFocusedCell(newVisualRowIndex, firstCellField);
          gridApi.ensureIndexVisible(newVisualRowIndex);
        }
      }, 100);
      return;
    }

    const newRow: RowData = {};
    // Initialize all columns with empty values
    displayColumnDefs.forEach(colDef => {
      const field = getColumnField(colDef);
      if (field) {
        // Set default values for logo blocks (serialized as JSON)
        if (isLogoBlock && field === 'col2') {
          newRow[field] = JSON.stringify({ scale: 100, xOffset: 0, yOffset: 0, autoCrop: false });
        } else {
          newRow[field] = '';
        }
      }
    });
    
    const updatedRowData = [...rowData, newRow];
    trackEvent('data_grid_row_added', {
      block_id: blockId,
      block_name: blockName,
      next_row_count: updatedRowData.length,
    });
    const result = onRowDataChange?.(updatedRowData);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onRowDataChange:', error);
      });
    }
    
    // Focus on the newly added row after a short delay to ensure the grid has updated
    setTimeout(() => {
      if (gridApi) {
        const newRowIndex = updatedRowData.length - 1;
        const firstField: string | undefined = getColumnField(displayColumnDefs[0]);
        if (firstField) {
          gridApi.setFocusedCell(newRowIndex, firstField);
        }
        gridApi.ensureIndexVisible(newRowIndex);
      }
    }, 100);
  };

  const removeSelectedRows = () => {
    if (!gridApi) return;
    
    const selectedNodes = gridApi.getSelectedNodes();
    if (selectedNodes.length === 0) return;

    if (wysiwygModel.enabled) {
      const selectedVisualRows: WysiwygVisualRow[] = selectedNodes
        .map((node: any) => node.data as WysiwygVisualRow | undefined)
        .filter((row: WysiwygVisualRow | undefined): row is WysiwygVisualRow => Boolean(row));
      const indexesToDelete: number[] = getWysiwygIndexesFromRows(selectedVisualRows);
      const updatedRowData: RowData[] = [...rowData];
      indexesToDelete.forEach((index: number) => {
        updatedRowData.splice(index, 1);
      });
      trackEvent('data_grid_rows_deleted', {
        block_id: blockId,
        block_name: blockName,
        deleted_count: indexesToDelete.length,
        next_row_count: updatedRowData.length,
        wysiwyg_multicolumn: true,
        wysiwyg_layout: wysiwygModel.layoutMode,
      });
      const result = onRowDataChange?.(updatedRowData);
      if (result instanceof Promise) {
        result.catch(error => {
          console.error('Error in onRowDataChange:', error);
        });
      }
      return;
    }
    
    const selectedIndexes = selectedNodes.map((node: any) => node.rowIndex).sort((a: number, b: number) => b - a);
    const updatedRowData = [...rowData];
    
    // Remove rows in reverse order to maintain correct indices
    selectedIndexes.forEach((index: number) => {
      updatedRowData.splice(index, 1);
    });

    trackEvent('data_grid_rows_deleted', {
      block_id: blockId,
      block_name: blockName,
      deleted_count: selectedIndexes.length,
      next_row_count: updatedRowData.length,
    });
    
    const result = onRowDataChange?.(updatedRowData);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onRowDataChange:', error);
      });
    }
  };

  const isCellDragTargetEditable = (colDef: ColDef | undefined, rowDataForCell: RowData | WysiwygVisualRow | undefined): boolean => {
    if (!colDef) return false;
    if (colDef.editable === false) return false;
    if (typeof colDef.editable === 'function') {
      try {
        return Boolean(colDef.editable({ data: rowDataForCell, colDef } as any));
      } catch {
        return false;
      }
    }
    return true;
  };

  const getEventTargetElement = (event: Event | null | undefined): HTMLElement | null =>
    event?.target instanceof HTMLElement ? event.target : null;

  const getCellElementFromGridEvent = (event: CellMouseDownEvent<RowData> | CellMouseOverEvent<RowData>): HTMLElement | null =>
    getEventTargetElement(event.event)?.closest('.ag-cell') as HTMLElement | null;

  const getDragCellFromGridEvent = (event: CellMouseDownEvent<RowData> | CellMouseOverEvent<RowData>): DataGridDragCell | null => {
    if (event.rowIndex === null || !event.data || !isCellDragTargetEditable(event.colDef as ColDef, event.data as RowData | WysiwygVisualRow)) {
      return null;
    }

    const colId: string = event.column.getColId();

    const sourceCell = wysiwygModel.enabled
      ? getWysiwygCellSource(
        rowData,
        event.data as WysiwygVisualRow,
        colId,
        wysiwygModel.sourceFields
      )
      : event.rowIndex < rowData.length
        ? {
            sourceIndex: event.rowIndex,
            sourceField: getColumnField(event.colDef) ?? colId,
          }
        : null;
    if (!sourceCell?.sourceField) return null;

    return {
      sourceIndex: sourceCell.sourceIndex,
      sourceField: sourceCell.sourceField,
      rowIndex: event.rowIndex,
      colId,
    };
  };

  const getVisualColumnOrder = (api: GridApi<RowData>): number[] => {
    const seen: Set<number> = new Set<number>();
    const visualColumnOrder: number[] = [];
    api.getAllDisplayedColumns().forEach((column) => {
      const visualColumnIndex: number | null = getWysiwygVisualColumnIndex(column.getColId(), wysiwygModel.sourceFields);
      if (visualColumnIndex === null || seen.has(visualColumnIndex)) return;
      seen.add(visualColumnIndex);
      visualColumnOrder.push(visualColumnIndex);
    });
    return visualColumnOrder;
  };

  const clearCellDragVisualState = (pendingCellDrag: PendingCellDrag | null): void => {
    pendingCellDrag?.sourceElement?.classList.remove('data-grid-cell-drag-source');
    pendingCellDrag?.lastDropTargetElement?.classList.remove('data-grid-cell-drop-target');
    document.body.classList.remove('data-grid-cell-drag-active');
  };

  const updateCellDragHover = (
    pendingCellDrag: PendingCellDrag,
    nextDropTargetElement: HTMLElement | null,
    nextDropTargetCell: DataGridDragCell | null
  ): void => {
    const targetElementToHighlight: HTMLElement | null = nextDropTargetCell ? nextDropTargetElement : null;

    if (pendingCellDrag.lastDropTargetElement && pendingCellDrag.lastDropTargetElement !== targetElementToHighlight) {
      pendingCellDrag.lastDropTargetElement.classList.remove('data-grid-cell-drop-target');
    }
    if (targetElementToHighlight) {
      targetElementToHighlight.classList.add('data-grid-cell-drop-target');
    }
    pendingCellDrag.lastDropTargetElement = targetElementToHighlight;
    pendingCellDrag.lastDropTargetCell = nextDropTargetCell;
  };

  const suppressNextCellDragClick = (): void => {
    const suppressUntil: number = Date.now() + 500;
    suppressCellDragClickUntilRef.current = suppressUntil;
    gridApi?.deselectAll();
    setTimeout(() => {
      if (suppressCellDragClickUntilRef.current === suppressUntil) {
        gridApi?.deselectAll();
        suppressCellDragClickUntilRef.current = 0;
      }
    }, 0);
  };

  const handleGridClickCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (Date.now() > suppressCellDragClickUntilRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    gridApi?.deselectAll();
  };

  const onColumnMoved = (event: ColumnMovedEvent<RowData>): void => {
    if (!event.finished || !wysiwygModel.enabled || !onRowDataChange) return;
    const visualColumnOrder: number[] = getVisualColumnOrder(event.api);
    if (visualColumnOrder.length <= 1) return;

    const reorderedRowData: RowData[] = reorderWysiwygColumns(
      rowData,
      wysiwygModel.columnCount,
      visualColumnOrder,
      wysiwygModel.reverseColumns
    );
    if (haveSameRowOrder(reorderedRowData, rowData)) return;

    trackEvent('data_grid_columns_reordered', {
      block_id: blockId,
      block_name: blockName,
      row_count: reorderedRowData.length,
      column_count: wysiwygModel.columnCount,
      wysiwyg_multicolumn: true,
      wysiwyg_layout: wysiwygModel.layoutMode,
    }, {
      dedupeKey: `${blockId ?? 'no_block'}:${visualColumnOrder.join(',')}:wysiwyg-columns`,
    });

    handleMaybePromise(onRowDataChange(reorderedRowData), 'Error in onRowDataChange:');
  };

  const handleCellMouseDown = (event: CellMouseDownEvent<RowData>): void => {
    const mouseEvent: MouseEvent | null = event.event instanceof MouseEvent ? event.event : null;
    const targetElement: HTMLElement | null = getEventTargetElement(event.event);
    if (!mouseEvent || mouseEvent.button !== 0 || targetElement?.closest('.ag-row-drag, .ag-cell-inline-editing')) return;

    const cell: DataGridDragCell | null = getDragCellFromGridEvent(event);
    pendingCellDragRef.current = cell
      ? {
          cell,
          startX: mouseEvent.clientX,
          startY: mouseEvent.clientY,
          isDragging: false,
          sourceElement: getCellElementFromGridEvent(event),
          lastDropTargetElement: null,
          lastDropTargetCell: null,
        }
      : null;
  };

  const handleGridPointerMoveCapture = (event: React.PointerEvent<HTMLDivElement>): void => {
    const pendingCellDrag: PendingCellDrag | null = pendingCellDragRef.current;
    if (!pendingCellDrag) return;

    const deltaX: number = event.clientX - pendingCellDrag.startX;
    const deltaY: number = event.clientY - pendingCellDrag.startY;
    if (!pendingCellDrag.isDragging && Math.hypot(deltaX, deltaY) < CELL_SWAP_DRAG_THRESHOLD_PX) return;

    if (!pendingCellDrag.isDragging) {
      pendingCellDrag.isDragging = true;
      pendingCellDrag.sourceElement?.classList.add('data-grid-cell-drag-source');
      document.body.classList.add('data-grid-cell-drag-active');
      gridApi?.deselectAll();
    }

    event.preventDefault();
    window.getSelection()?.removeAllRanges();
  };

  const handleGridPointerUpCapture = (event: React.PointerEvent<HTMLDivElement>): void => {
    const pendingCellDrag: PendingCellDrag | null = pendingCellDragRef.current;
    pendingCellDragRef.current = null;
    clearCellDragVisualState(pendingCellDrag);
    if (pendingCellDrag?.isDragging) {
      suppressNextCellDragClick();
    }
    if (!pendingCellDrag || !pendingCellDrag.isDragging || !onRowDataChange) return;

    event.preventDefault();
    window.getSelection()?.removeAllRanges();
    const destinationCell: DataGridDragCell | null = pendingCellDrag.lastDropTargetCell;
    if (!destinationCell) return;

    const updatedRowData: RowData[] | null = swapRowCellValues(rowData, pendingCellDrag.cell, destinationCell);
    if (!updatedRowData) return;

    trackEvent('data_grid_cells_swapped', {
      block_id: blockId,
      block_name: blockName,
      source_row_index: pendingCellDrag.cell.sourceIndex,
      source_field_name: pendingCellDrag.cell.sourceField,
      destination_row_index: destinationCell.sourceIndex,
      destination_field_name: destinationCell.sourceField,
      wysiwyg_multicolumn: wysiwygModel.enabled,
      wysiwyg_layout: wysiwygModel.enabled ? wysiwygModel.layoutMode : undefined,
    }, {
      dedupeKey: `${blockId ?? 'no_block'}:${pendingCellDrag.cell.sourceIndex}:${pendingCellDrag.cell.sourceField}:${destinationCell.sourceIndex}:${destinationCell.sourceField}`,
    });

    handleMaybePromise(onRowDataChange(updatedRowData), 'Error in onRowDataChange:');
    setTimeout(() => {
      if (!gridApi) return;
      gridApi.setFocusedCell(destinationCell.rowIndex, destinationCell.colId);
      gridApi.ensureIndexVisible(destinationCell.rowIndex);
    }, 50);
  };

  const handleCellMouseOver = (event: CellMouseOverEvent<RowData>): void => {
    const pendingCellDrag: PendingCellDrag | null = pendingCellDragRef.current;
    if (!pendingCellDrag?.isDragging) return;

    const targetElement: HTMLElement | null = getCellElementFromGridEvent(event);
    const targetCell: DataGridDragCell | null =
      targetElement && targetElement !== pendingCellDrag.sourceElement
        ? getDragCellFromGridEvent(event)
        : null;
    updateCellDragHover(pendingCellDrag, targetElement, targetCell);
  };



  const onRowDragEnd = (event: RowDragEndEvent<RowData>): void => {
    if (!onRowDataChange) return;

    if (wysiwygModel.enabled) {
      const reorderedVisualRows: WysiwygVisualRow[] = [];
      event.api.forEachNode((node) => {
        if (node.data) {
          reorderedVisualRows.push(node.data as WysiwygVisualRow);
        }
      });
      if (reorderedVisualRows.length !== displayRowData.length) return;
      const reorderedRowData: RowData[] = reorderWysiwygChunks(reorderedVisualRows, rowData);

      trackEvent('data_grid_rows_reordered', {
        block_id: blockId,
        block_name: blockName,
        row_count: reorderedRowData.length,
        wysiwyg_multicolumn: true,
        wysiwyg_layout: wysiwygModel.layoutMode,
      }, {
        dedupeKey: `${blockId ?? 'no_block'}:${reorderedRowData.length}:wysiwyg`,
      });

      const result: void | Promise<void> = onRowDataChange(reorderedRowData);
      if (result instanceof Promise) {
        result.catch((error: unknown) => {
          console.error('Error in onRowDataChange:', error);
        });
      }
      return;
    }

    const reorderedRowData: RowData[] = [];
    event.api.forEachNode((node) => {
      if (node.data) {
        reorderedRowData.push(node.data);
      }
    });

    if (reorderedRowData.length !== rowData.length) return;

    trackEvent('data_grid_rows_reordered', {
      block_id: blockId,
      block_name: blockName,
      row_count: reorderedRowData.length,
    }, {
      dedupeKey: `${blockId ?? 'no_block'}:${reorderedRowData.length}`,
    });

    const result: void | Promise<void> = onRowDataChange(reorderedRowData);
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        console.error('Error in onRowDataChange:', error);
      });
    }
  };

  const selectVerticalDragPair = (rowIndex: number): void => {
    if (!gridApi || !wysiwygModel.enabled || wysiwygModel.layoutMode !== 'vertical') return;

    const currentNode = gridApi.getDisplayedRowAtIndex(rowIndex);
    const visualRow: WysiwygVisualRow | undefined = currentNode?.data as WysiwygVisualRow | undefined;
    if (!currentNode || visualRow?.__wysiwygSourceField !== wysiwygModel.sourceFields[0]) return;

    const pairedNode = gridApi.getDisplayedRowAtIndex(rowIndex + 1);
    const pairedVisualRow: WysiwygVisualRow | undefined = pairedNode?.data as WysiwygVisualRow | undefined;
    if (!pairedNode || pairedVisualRow?.__wysiwygSourceField !== wysiwygModel.sourceFields[1]) return;

    gridApi.deselectAll();
    currentNode.setSelected(true);
    pairedNode.setSelected(true);
  };

  const handleGridMouseDownCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!wysiwygModel.enabled || wysiwygModel.layoutMode !== 'vertical') return;

    const target = event.target as HTMLElement | null;
    const cell = target?.closest('.ag-cell') as HTMLElement | null;
    if (!cell || cell.getAttribute('col-id') !== '__wysiwygRowLabel') return;

    const row = cell.closest('.ag-row') as HTMLElement | null;
    const rowIndexRaw: string | null | undefined = row?.getAttribute('row-index');
    const rowIndex: number = Number(rowIndexRaw);
    if (!Number.isInteger(rowIndex)) return;

    selectVerticalDragPair(rowIndex);
  };

  const onGridReady = (params: GridReadyEvent<RowData>): void => {
    setGridApi(params.api);
    
    // Force the grid to render properly
    if (gridReadyTimeoutRef.current) {
      clearTimeout(gridReadyTimeoutRef.current);
    }
    gridReadyTimeoutRef.current = setTimeout(() => {
      params.api.sizeColumnsToFit();
      params.api.refreshCells({ force: true });
      
      // Sometimes a manual redraw helps with display issues
      window.dispatchEvent(new Event('resize'));
      gridReadyTimeoutRef.current = null;
    }, 100);
  };

  return (
    <>
      <Dialog open={isUrlDialogOpen} onClose={handleUrlDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Enter logo URL</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL (http:// or https://)"
            type="url"
            fullWidth
            value={urlInputValue}
            onChange={(e) => setUrlInputValue(e.target.value)}
            error={!!urlInputError}
            helperText={urlInputError || 'Enter a direct URL to the logo image or resource'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUrlDialogClose}>Cancel</Button>
          <Button onClick={handleUrlDialogSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      <div 
        className={`ag-theme-alpine data-grid-container${wysiwygModel.layoutMode === 'vertical' && wysiwygModel.enabled ? ' data-grid-container-vertical' : ''}`}
        onMouseDownCapture={handleGridMouseDownCapture}
        onClickCapture={handleGridClickCapture}
        onPointerMoveCapture={handleGridPointerMoveCapture}
        onPointerUpCapture={handleGridPointerUpCapture}
        onPointerCancelCapture={() => {
          clearCellDragVisualState(pendingCellDragRef.current);
          pendingCellDragRef.current = null;
        }}
        style={{ 
          height: '100%', 
          width: '100%', 
          border: 'none', // Remove inline border
          display: 'block',
          overflow: 'hidden'
        }}
      >
        <AgGridReact<RowData>
          rowData={displayRowData as RowData[]}
          columnDefs={displayColumnDefs}
          pagination={false}
          rowDragManaged={true}
          rowDragMultiRow={wysiwygModel.enabled && wysiwygModel.layoutMode === 'vertical'}
          animateRows={true}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onCellValueChanged={onUpdateData}
          onCellMouseDown={handleCellMouseDown}
          onCellMouseOver={handleCellMouseOver}
          onRowDragEnd={onRowDragEnd}
          onColumnMoved={onColumnMoved}
          suppressDragLeaveHidesColumns={true}
          suppressMoveWhenColumnDragging={true}
          suppressCellFocus={false}
          suppressRowHoverHighlight={false}
          enableCellTextSelection={true}
          rowSelection={rowSelection}
          suppressClipboardApi={false}
          suppressContextMenu={true}
          sideBar={false}
          cellSelection={true}
          suppressMultiSort={true}
        />
      </div>
      
      <div className="data-grid-table" style={{ marginTop: '10px', fontSize: '12px' }}>
        <div style={{ color: '#666' }}>
          {(rowData.length > 0 ? `Showing ${rowData.length} rows from the sheet` : 'No sheet data to display')}
        </div>
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button 
            onClick={addRow}
            className="data-grid-action-btn"
            title="Add new row"
          >
            + Add Row
          </button>
          <button 
            onClick={removeSelectedRows}
            className="data-grid-action-btn delete"
            title="Delete selected rows"
          >
            × Delete Selected
          </button>
        </div>
      </div>
    </>
  );
};

export default DataGrid;
